import { FunctionalComponent, VNode } from 'preact';
import { useLayoutEffect } from 'preact/hooks';
import { Router, Route, lazy } from 'preact-iso';
import { logger } from '@accedo/logger';
import { TestPage } from '##/pages/TestPage';
import { IconTestPage } from '##/pages/IconTestPage';
import { NotFoundPage } from '##/pages/NotFoundPage';
import { Route as ElevateRoute } from '##/dataModels/route';
import { PageTemplates } from '##/dataModels/pageLayout';
import { config } from '##/services/store';
import { onRouteChange, onLoadStart, onLoadEnd, addPublicPath } from './utils';

// dynamic page imports
const ModularPage = lazy<FunctionalComponent>(() => import('##/pages/ModularPage/ModularPage'));
const PlayerPage = lazy<FunctionalComponent>(() => import('##/pages/PlayerPage/PlayerPage'));
const LandingPage = lazy<FunctionalComponent>(() => import('##/pages/LandingPage/LandingPage'));

const SearchPage = lazy<FunctionalComponent>(() => import('##/pages/SearchPage/SearchPage'));
const MovieDetailsPage = lazy<FunctionalComponent>(() => import('##/pages/MovieDetailsPage/MovieDetailsPage'));
const SettingsPage = lazy<FunctionalComponent>(() => import('##/pages/SettingsPage/SettingsPage'));
const ShowDetailsPage = lazy<FunctionalComponent>(() => import('##/pages/ShowDetailsPage/ShowDetailsPage'));
const RegistrationPage = lazy<FunctionalComponent>(() => import('##/pages/RegistrationPage/RegistrationPage'));

const CategoryPage = lazy<FunctionalComponent>(() => import('##/pages/CategoryPage/CategoryPage'));

// Elevate template to page component mapping
const templateToPageComponent: Record<PageTemplates, FunctionalComponent> = {
  [PageTemplates.modular]: ModularPage,
  [PageTemplates.signup]: RegistrationPage,
  [PageTemplates.signin]: RegistrationPage,
  [PageTemplates.collection]: NotFoundPage,
  [PageTemplates.myContent]: ModularPage,
  [PageTemplates.search]: SearchPage,
  [PageTemplates.info]: SettingsPage,
  [PageTemplates.player]: PlayerPage,
  [PageTemplates.category]: CategoryPage,
  [PageTemplates.movieDetail]: MovieDetailsPage,
  [PageTemplates.home]: ModularPage,
  [PageTemplates.viewAll]: ModularPage,
};

// Log Prefix
const LP = '[AppRouter]';

/*
Uses https://github.com/preactjs/preact-iso
*/
export const AppRouter: FunctionalComponent = () => {
  const elevateRoutes: ElevateRoute[] = config.getByKey<ElevateRoute[]>('routes');

  // dynamic pages
  const kids: VNode[] = elevateRoutes
    ? elevateRoutes.map<VNode>((elevateRoute) => {
        // Workaround: Override component for search route if backend sends wrong template
        let component = templateToPageComponent[elevateRoute.page.template];
        if (elevateRoute.route === '/search' && component !== SearchPage) {
          console.warn(`${LP} Overriding component for /search route to use SearchPage instead of ${component?.name}`);
          component = SearchPage;
        }

        console.log(
          `${LP} Mapping route "${elevateRoute.route}" to template "${elevateRoute.page.template}" in component "${component?.name}"`,
        );
        if (!component) {
          logger.warn(
            `${LP} No component mapping found for template "${elevateRoute.page.template}" on route "${elevateRoute.route}". Using NotFoundPage as fallback.`,
          );
        }
        return <Route path={addPublicPath(elevateRoute.route)} component={component || NotFoundPage} />;
      })
    : [];
  // static pages
  const staticRoutes: VNode[] = [
    <Route component={NotFoundPage} default />,
    <Route path={addPublicPath('/landing')} component={LandingPage} />,
    <Route path={addPublicPath('/movie/:id')} component={MovieDetailsPage} />,
    <Route path={addPublicPath('/short-film/:id')} component={MovieDetailsPage} />,
    <Route path={addPublicPath('/show/:id')} component={ShowDetailsPage} />,
    <Route path={addPublicPath('/login')} component={RegistrationPage} />,
    <Route path={addPublicPath('/signup')} component={RegistrationPage} />,
    <Route path={addPublicPath('/category/:categoryId')} component={CategoryPage} />,
    <Route path={addPublicPath('/genre/:genreId')} component={CategoryPage} />,
    <Route path={addPublicPath('/player/:assetType/:id')} component={PlayerPage} />,
    <Route path={addPublicPath('/single/:id')} component={PlayerPage} />,
    <Route path={addPublicPath('/highlight/:id')} component={PlayerPage} />,
    <Route path={addPublicPath('/eventvod/:id')} component={PlayerPage} />,
    <Route path={addPublicPath('/settings')} component={SettingsPage} />,
    // Add your own static pages here for development if needed
    <Route path={addPublicPath('/test')} component={TestPage} />,
    <Route path={addPublicPath('/testIcons')} component={IconTestPage} />,
  ];

  // Combine dynamic and static routes
  kids.push(...staticRoutes);

  useLayoutEffect(() => {
    logger.debug(`${LP} AppRouter useLayoutEffect`);
  }, []);

  return (
    <Router onRouteChange={onRouteChange} onLoadStart={onLoadStart} onLoadEnd={onLoadEnd}>
      {kids}
    </Router>
  );
};
